import { AppBar, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { shallowEqual } from 'react-redux';

import { CLIENT_DETAILS_PAGE, CRM_CLIENTS_PAGE } from 'src/constants';
import {
  BANNER_HEIGHT,
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
  SIDEBAR_PANEL_MIN_WIDTH,
} from 'src/constants/uiConsts';
import { RouteContext } from 'src/context';
import { ContextBarLayout } from 'src/features/context-bar/ContextBarLayout';
import { ResizableGroup } from 'src/features/resizableWrapper/ResizableGroup';
import { ResizablePanel } from 'src/features/resizableWrapper/ResizablePanel';
import { ResizeHandle } from 'src/features/resizableWrapper/ResizeHandle';
import { useElectronSidebar } from 'src/hooks/useElectronSidebar';
import { useImportProgressNotifier } from 'src/hooks/useImportProgressNotifier';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useWindowSize } from 'src/hooks/useWindowResize';
import AuthModal from 'src/legacy/components/Auth/AuthModal';
import { Banner } from 'src/legacy/components/Banner';
import { CommandBar } from 'src/legacy/components/CommandBar';
import { GeneralCommandbarActionsInitializer } from 'src/legacy/components/CommandBar/GeneralCommandbarActionsInitializer';
import { CompleteExternalActionModal } from 'src/legacy/components/Modals';
import Navbar from 'src/legacy/components/Navbar';
import { ShortcutsSidebar } from 'src/legacy/components/ShortcutsDrawer';
import Sidebar from 'src/legacy/components/Sidebar';
import { WhatsNew } from 'src/legacy/components/WhatsNew/WhatsNew';
import { RootState } from 'src/store/reduxTypes';
import { updateSearchValueAction } from 'src/store/ui/actions';
import * as Colors from 'src/theme/colors';

import { globalCss } from '../globalCss';
import { HelpCenterModal } from '../HelpCenterModal';

import { HeaderSlotProvider, useHeaderSlot } from './HeaderSlotContext';
import { ShellProvider, useShell } from './ShellContext';

const PAGES_WITH_RETAINED_SEARCH = [
  CRM_CLIENTS_PAGE.path,
  CLIENT_DETAILS_PAGE.path,
];

const SIDEBAR_MIN_PX = SIDEBAR_PANEL_MIN_WIDTH;
const SIDEBAR_DEFAULT_PX = SIDEBAR_PANEL_MIN_WIDTH;
const SIDEBAR_MAX_PX = 330;

/**
 * Inner component that consumes ShellContext and HeaderSlotContext.
 * Separated so the providers are established before any consumer hooks run.
 */
const AppShellInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    drawerWidth,
    sidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
    showContextBar,
    contextBarContentMinWidth,
    isResizableSidebarActive,
    isDesktopSidebarCollapsed,
    sidebarPanelRef,
    onSidebarPanelResize,
    onSidebarCollapse,
    onSidebarExpand,
  } = useShell();
  const { headerSlot, ignoreHeaderPadding, showSidebar } = useHeaderSlot();
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { width: viewportWidth } = useWindowSize();

  // Sync sidebar state with the Electron chrome bar toggle button
  useElectronSidebar();

  const sidebarMinPercent =
    viewportWidth > 0 ? (SIDEBAR_MIN_PX / viewportWidth) * 100 : 15;
  const sidebarDefaultPercent =
    viewportWidth > 0 ? (SIDEBAR_DEFAULT_PX / viewportWidth) * 100 : 20;
  const sidebarMaxPercent =
    viewportWidth > 0 ? (SIDEBAR_MAX_PX / viewportWidth) * 100 : 30;

  // Breakpoints matching the original Layout's responsive behavior:
  // down('xs') = max-width 599.95px (mobile navbar height)
  // up('md') = min-width 960px (desktop drawer padding)
  const isXsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    loaded: userLoaded,
    loading: userLoading,
    isClient,
  } = useAppSelector(
    (state: RootState) => ({
      loaded: state.user.loaded,
      loading: state.user.loading,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  const includeContextBar = showContextBar && !isClient;

  const bannerOptions = useAppSelector(
    (state: RootState) => state.ui.bannerOptions,
  );

  const showBanner = Boolean(bannerOptions);

  // headerSlot === undefined → no page has set a header (show default navbar)
  // headerSlot === null → page explicitly hides navbar (fullbleed)
  // headerSlot === <ReactNode> → custom header content
  const hasNavbar = headerSlot !== null;

  const navbarHeight = isXsDown
    ? MOBILE_APP_BAR_HEIGHT
    : includeContextBar
      ? 60
      : DESKTOP_APP_BAR_HEIGHT;
  const appBarHeight =
    (hasNavbar ? navbarHeight : 0) + (showBanner ? BANNER_HEIGHT : 0);

  const appBarStyle: React.CSSProperties = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: 'none',
    zIndex: theme.zIndex.drawer + 1,
    borderBottom: hasNavbar
      ? `1px solid ${Colors.DividersAndCardBorders}`
      : 'none',
    height: appBarHeight,
    paddingLeft: isMdUp && showSidebar ? drawerWidth : 0,
  };
  if (!includeContextBar) {
    appBarStyle.left = '50%';
    appBarStyle.transform = 'translateX(-50%)';
  }

  const resizableAppBarStyle: React.CSSProperties = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: 'none',
    borderBottom: hasNavbar
      ? `1px solid ${Colors.DividersAndCardBorders}`
      : 'none',
    height: appBarHeight,
  };

  // Header slot cleanup is handled by PageShell and LayoutShim unmount effects.
  // An AppShell-level clearHeader on pathname change would race against child
  // mount effects (React fires child effects before parent effects), overriding
  // the new page's setHeader call.

  // Search bar should persist query between client list and client details.
  // Reset when navigating away from those pages.
  React.useEffect(() => {
    if (!PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))) {
      dispatch(updateSearchValueAction(''));
    }
    return () => {
      if (
        !PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))
      ) {
        dispatch(updateSearchValueAction(''));
      }
    };
  }, [pathname, dispatch]);

  // Reset scroll position when navigating between pages.
  // In non-panel mode, rootRef's div is the scroll container (overflowY: auto).
  // In panel mode, the content wrapper div (contentRef) is the scroll container.
  React.useEffect(() => {
    const target =
      isResizableSidebarActive && showSidebar
        ? contentRef.current
        : rootRef.current;
    if (target) {
      target.scrollTop = 0;
    }
  }, [pathname, isResizableSidebarActive, showSidebar]);

  // Keep Redux sidebarOpen in sync with the resizable panel's collapsed state.
  // Only relevant when the resizable sidebar flag is active.
  React.useLayoutEffect(() => {
    if (!isResizableSidebarActive) return;

    if (isDesktopSidebarCollapsed && sidebarOpen) {
      closeMobileSidebar();
    } else if (!isDesktopSidebarCollapsed && !sidebarOpen) {
      openMobileSidebar();
    }
  }, [
    isResizableSidebarActive,
    isDesktopSidebarCollapsed,
    sidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
  ]);

  useImportProgressNotifier();

  const showPrimarySidebarMobile = React.useCallback(() => {
    openMobileSidebar();
  }, [openMobileSidebar]);

  const hidePrimarySidebarMobile = React.useCallback(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar]);

  const shellContent = (
    <>
      <AppBar
        position={includeContextBar ? 'sticky' : 'fixed'}
        style={appBarStyle}
      >
        {bannerOptions ? <Banner {...bannerOptions} /> : null}
        {hasNavbar && (
          <Navbar
            userLoaded={userLoaded}
            isClient={isClient}
            userLoading={userLoading}
            onOpenSidebarMobile={showPrimarySidebarMobile}
            showSearchbar={false}
            showNavbarAuthActions
            headerContent={headerSlot || undefined}
            ignoreHeaderPadding={ignoreHeaderPadding}
          />
        )}
      </AppBar>
      {showSidebar && (
        <Sidebar
          onMobileClose={hidePrimarySidebarMobile}
          openMobile={sidebarOpen}
        />
      )}
      {children}
    </>
  );

  const panelNavbar = hasNavbar && (
    <Navbar
      userLoaded={userLoaded}
      isClient={isClient}
      userLoading={userLoading}
      onOpenSidebarMobile={showPrimarySidebarMobile}
      showSearchbar={false}
      showNavbarAuthActions
      headerContent={headerSlot || undefined}
      ignoreHeaderPadding={ignoreHeaderPadding}
    />
  );

  const panelAppBar = (
    <AppBar position="sticky" style={resizableAppBarStyle}>
      {bannerOptions ? <Banner {...bannerOptions} /> : null}
      {panelNavbar}
    </AppBar>
  );

  return (
    <div
      ref={rootRef}
      className="flex h-dvh box-border"
      style={{
        fontFamily: theme.typography.fontFamily,
        ...(isResizableSidebarActive && showSidebar
          ? {}
          : {
              paddingTop: includeContextBar ? undefined : appBarHeight,
              overflowY: includeContextBar
                ? undefined
                : isXsDown
                  ? 'hidden'
                  : 'auto',
            }),
      }}
    >
      <CommandBar>
        {isResizableSidebarActive && showSidebar ? (
          <ResizableGroup
            widthPersistKey="primary-sidebar"
            className="h-full w-full flex"
          >
            <ResizablePanel
              className="z-[1]"
              collapsible
              collapsedSize={0}
              minSize={sidebarMinPercent}
              defaultSize={sidebarDefaultPercent}
              maxSize={sidebarMaxPercent}
              ref={sidebarPanelRef}
              onResize={onSidebarPanelResize}
              onCollapse={onSidebarCollapse}
              onExpand={onSidebarExpand}
            >
              <Sidebar
                panelMode
                onMobileClose={hidePrimarySidebarMobile}
                openMobile={sidebarOpen}
              />
            </ResizablePanel>
            <ResizeHandle className="z-[1]" />
            <ResizablePanel className="flex flex-col min-h-0 overflow-hidden">
              {panelAppBar}
              <div
                ref={contentRef}
                className="overflow-y-auto flex-1 min-h-0 flex flex-col"
              >
                {includeContextBar ? (
                  <ContextBarLayout contentMinWidth={contextBarContentMinWidth}>
                    {children}
                  </ContextBarLayout>
                ) : (
                  children
                )}
              </div>
            </ResizablePanel>
          </ResizableGroup>
        ) : includeContextBar ? (
          <ContextBarLayout contentMinWidth={contextBarContentMinWidth}>
            {shellContent}
          </ContextBarLayout>
        ) : (
          shellContent
        )}
        <HelpCenterModal />
        <AuthModal />
        {userLoaded && (
          <>
            <CompleteExternalActionModal />
            <GeneralCommandbarActionsInitializer />
            <WhatsNew />
          </>
        )}
        <ShortcutsSidebar />
      </CommandBar>
    </div>
  );
};

export interface AppShellProps {
  children: React.ReactNode;
}

const APPSHELL_RESET_ID = 'appshell-reset';

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  // Inject critical CSS resets that the design-system normally provides via
  // an inline <style> tag in production SSR. On local dev the design-system
  // rules live inside a @layer and don't override browser defaults reliably.
  React.useLayoutEffect(() => {
    if (document.getElementById(APPSHELL_RESET_ID)) return;
    const style = document.createElement('style');
    style.id = APPSHELL_RESET_ID;
    style.textContent = globalCss.join('');
    document.head.appendChild(style);
  }, []);

  return (
    <ShellProvider>
      <HeaderSlotProvider>
        <AppShellInner>{children}</AppShellInner>
      </HeaderSlotProvider>
    </ShellProvider>
  );
};

export default AppShell;
